import { type JSX, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Icon } from '@/components/icons';
import { Button, Input, Spinner } from '@/components/ui';
import { api, ApiError } from '@/lib/api';

const schema = z
  .object({
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(8, 'Confirm your password'),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof schema>;

/**
 * Reset-password landing page. The :token URL param carries the raw
 * reset token from the email link; the form posts {token, new_password}
 * to /v1/auth/reset-password.
 *
 * Successful reset → bounce to /login with a brief success state. Any
 * api error (invalid_token, validation_error, network) is surfaced
 * inline; we don't differentiate "expired" vs "used" — the api
 * collapses them to the same blank-wall response.
 */
export default function ResetPasswordPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormValues>({ defaultValues: { newPassword: '', confirmPassword: '' } });

  if (!token) {
    return (
      <div className="w-full min-h-screen grid place-items-center bg-paper px-6">
        <div className="w-full max-w-[400px] text-center">
          <h1 className="display text-[24px] font-semibold text-text">Invalid reset link</h1>
          <p className="text-[14px] text-text-3 mt-2 mb-6">
            The link you followed is missing the token. Request a new one to continue.
          </p>
          <Link to="/forgot-password" className="text-[14px] text-volt-500 font-medium hover:underline">
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  const onSubmit = handleSubmit(async (values) => {
    setErrorMsg(null);
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (path === 'newPassword' || path === 'confirmPassword') {
          setError(path, { message: issue.message });
        }
      }
      return;
    }
    try {
      await api.post<void>('/v1/auth/reset-password', {
        token,
        new_password: parsed.data.newPassword,
      });
      setSubmitted(true);
      // After 2s redirect to login so the success state is visible
      // briefly. Long enough to read, short enough to feel responsive.
      window.setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'invalid_token') {
          setErrorMsg(
            'This reset link is invalid or has expired. Request a new one to continue.',
          );
        } else {
          // Other 4xx (validation, malformed JSON) and any 5xx fall here.
          // We surface a generic message rather than mapping `err.status`
          // to a copy variant — the precise reason is in the dev console
          // and the user's only useful action is to retry or report it.
          setErrorMsg('Something went wrong. Please try again in a moment.');
        }
      } else {
        setErrorMsg('Network error. Check your connection and retry.');
      }
    }
  });

  return (
    <div className="w-full min-h-screen grid place-items-center bg-paper px-6">
      <div className="w-full max-w-[400px]">
        <h1 className="display text-[28px] font-semibold tracking-[-0.02em] text-text leading-[1.15] m-0">
          Set a new password
        </h1>
        <p className="text-[14.5px] text-text-3 mt-2.5 mb-7 leading-relaxed">
          Choose a password you don't use anywhere else. After resetting, you'll be signed out
          everywhere and need to sign in again.
        </p>

        {submitted ? (
          <div
            role="status"
            className="px-4 py-4 bg-success-bg border border-success-bd rounded-md text-[14px] text-success leading-relaxed"
          >
            <strong className="block mb-1">Password updated</strong>
            Redirecting you to sign in…
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            {errorMsg && (
              <div
                role="alert"
                className="flex gap-2.5 items-start px-3.5 py-3 bg-danger-bg border border-danger-bd rounded-md mb-5"
              >
                <Icon.warn width="18" height="18" className="text-danger flex-shrink-0 mt-px" />
                <div className="text-[13px] text-danger leading-relaxed">
                  {errorMsg}{' '}
                  <Link
                    to="/forgot-password"
                    className="underline font-medium ml-0.5"
                  >
                    Request a new link
                  </Link>
                </div>
              </div>
            )}

            <div className="mb-4">
              <label
                htmlFor="reset-new-password"
                className="block text-[12px] font-medium text-text-2 mb-1.5"
              >
                New password
              </label>
              <Input
                id="reset-new-password"
                type="password"
                autoComplete="new-password"
                disabled={isSubmitting}
                error={Boolean(errors.newPassword)}
                {...register('newPassword')}
              />
              {errors.newPassword && (
                <p className="text-[12px] text-danger mt-1.5">{errors.newPassword.message}</p>
              )}
            </div>

            <div className="mb-5">
              <label
                htmlFor="reset-confirm-password"
                className="block text-[12px] font-medium text-text-2 mb-1.5"
              >
                Confirm new password
              </label>
              <Input
                id="reset-confirm-password"
                type="password"
                autoComplete="new-password"
                disabled={isSubmitting}
                error={Boolean(errors.confirmPassword)}
                {...register('confirmPassword')}
              />
              {errors.confirmPassword && (
                <p className="text-[12px] text-danger mt-1.5">{errors.confirmPassword.message}</p>
              )}
            </div>

            <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner />
                  <span>Updating…</span>
                </>
              ) : (
                <span>Update password</span>
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
